import ExcelJS from "exceljs";
import { saveAs } from 'file-saver';
import moment from "moment";
import apiClient from "@/http-common";

const formatCurrency = (value) => {
  if (!value && value !== 0) return "";
  return new Intl.NumberFormat('vi-VN').format(value);
};

const formatTax = (val) => {
  if (val === null || val === undefined || val === '') return '';
  const tax = Number(val);
  if (tax === -2) return 'Không chịu thuế';
  if (tax === -1) return 'Không kê khai tính/nộp thuế';
  return `${tax}%`;
};

async function getBase64FromUrl(url) {
  if (!url) return null;
  if (url.startsWith('data:')) {
    return url.split(',')[1];
  }
  
  let targetUrl = url;
  if (url.startsWith('//')) {
    targetUrl = window.location.protocol + url;
  } else if (!url.startsWith('http://') && !url.startsWith('https://')) {
    const apiBe = process.env.VUE_APP_API_BE || '';
    targetUrl = apiBe + (url.startsWith('/') ? '' : '/') + url;
  }

  try {
    // Try fetching with apiClient (Axios) which automatically has the token and proper CORS headers
    const response = await apiClient.get(targetUrl, { responseType: 'blob' });
    const blob = response.data;
    if (!blob) return null;
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Url = reader.result;
        const base64Data = base64Url.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("apiClient fetch failed, trying fallback fetch:", error);
    try {
      // Fallback to standard fetch
      const headers = {};
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.accessToken) {
        headers['Authorization'] = 'Bearer ' + user.accessToken;
      }
      const response = await fetch(targetUrl, { headers });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Url = reader.result;
          const base64Data = base64Url.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (fallbackError) {
      console.warn("Fallback fetch with auth failed, trying simple fetch without headers:", fallbackError);
      try {
        const response = await fetch(targetUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64Url = reader.result;
            const base64Data = base64Url.split(',')[1];
            resolve(base64Data);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (simpleFetchError) {
        console.warn("Simple fetch failed, trying canvas fallback:", simpleFetchError);
        try {
          return await new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL('image/png').split(',')[1]);
            };
            img.onerror = reject;
            img.src = targetUrl;
          });
        } catch (canvasError) {
          console.warn("Canvas method failed, trying CORS proxy:", canvasError);
          try {
            // Sử dụng CORS Proxy công khai làm giải pháp cuối cùng
            const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(targetUrl)}`;
            const response = await fetch(proxyUrl);
            if (!response.ok) throw new Error(`Proxy HTTP error! status: ${response.status}`);
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                resolve(reader.result.split(',')[1]);
              };
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          } catch (proxyError) {
            console.error("All methods failed for Excel image, including CORS proxy:", targetUrl, proxyError);
            return null;
          }
        }
      }
    }
  }
}

export async function exportTuitionInvoiceToExcel({
  invoices = [],
  companyName = "ONEKIDS-ONECAM",
  companyAddress = "62 Trương Công Giai, Cầu Giấy, Hà Nội, Việt Nam",
  bankAccount = "",
  bankOwner = "",
  bankName = "",
  schoolLogo = "",
  showTime = false,
  calcWords = () => ""
}) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = companyName;
  workbook.lastModifiedBy = companyName;
  workbook.created = new Date();
  workbook.modified = new Date();

  const logoBase64 = await getBase64FromUrl(schoolLogo);

  for (let index = 0; index < invoices.length; index++) {
    const invoiceInfo = invoices[index];
    // Tên sheet giới hạn 31 ký tự, loại bỏ ký tự đặc biệt
    let sheetName = `PhieuThu_${invoiceInfo.fullName || index + 1}`;
    sheetName = sheetName.replace(/[\\/*?:[\]]/g, '').substring(0, 31);
    
    // Đảm bảo tên sheet duy nhất
    let count = 1;
    let finalSheetName = sheetName;
    while(workbook.getWorksheet(finalSheetName)) {
      finalSheetName = `${sheetName.substring(0, 28)}_${count}`;
      count++;
    }

    const worksheet = workbook.addWorksheet(finalSheetName, {
      pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
    });

    // Cấu hình độ rộng cột
    worksheet.columns = [
      { key: 'col1', width: 6 },  // STT
      { key: 'col2', width: 28 }, // Tên khoản thu
      { key: 'col3', width: 8 },  // ĐVT
      { key: 'col4', width: 6 },  // SL
      { key: 'col5', width: 14 }, // Đơn giá
      { key: 'tax', width: 12 },  // Thuế
      { key: 'col6', width: 11 }, // Giảm giá
      { key: 'col7', width: 14 }, // Thành tiền
      { key: 'col8', width: 22 }  // Ghi chú
    ];

    const addEmptyRow = () => worksheet.addRow([]);

    // --- Header ---
    const row1 = worksheet.addRow([null, null, companyName]);
    row1.height = 25;
    row1.getCell(3).font = { name: 'Times New Roman', bold: true, size: 14 };
    row1.getCell(3).alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.mergeCells(`C${row1.number}:G${row1.number}`);

    const row2 = worksheet.addRow([null, null, companyAddress]);
    row2.height = 25;
    row2.getCell(3).font = { name: 'Times New Roman', size: 11, italic: true };
    row2.getCell(3).alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.mergeCells(`C${row2.number}:G${row2.number}`);

    const rowTitle = worksheet.addRow([null, null, "PHIẾU THU HỌC PHÍ"]);
    rowTitle.height = 35;
    rowTitle.getCell(3).font = { name: 'Times New Roman', bold: true, size: 16 };
    rowTitle.getCell(3).alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.mergeCells(`C${rowTitle.number}:G${rowTitle.number}`);

    const subtitle = `Tháng ${invoiceInfo.month || '...'} / ${invoiceInfo.year || '...'} - Đợt ${invoiceInfo.turn || '...'}`;
    const rowSubTitle = worksheet.addRow([null, null, subtitle]);
    rowSubTitle.height = 25;
    rowSubTitle.getCell(3).font = { name: 'Times New Roman', size: 12, italic: true };
    rowSubTitle.getCell(3).alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.mergeCells(`C${rowSubTitle.number}:G${rowSubTitle.number}`);

    // --- Setup Logo and QR areas (merge & white background to hide gridlines) ---
    for (let r = 1; r <= 4; r++) {
      worksheet.getCell(`A${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
      worksheet.getCell(`B${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
      worksheet.getCell(`H${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
      worksheet.getCell(`I${r}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
    }
    worksheet.mergeCells('A1:B4');
    worksheet.mergeCells('H1:I4');

    // --- Add Logo and QR ---
    if (logoBase64) {
      try {
        const logoImageId = workbook.addImage({
          base64: logoBase64,
          extension: 'png',
        });
        worksheet.addImage(logoImageId, {
          tl: { col: 1.15, row: 0.35 },
          ext: { width: 90, height: 90 }
        });
      } catch (e) {
        console.error("Error adding logo image to sheet:", e);
      }
    }

    const qrBase64 = await getBase64FromUrl(invoiceInfo.urlOnlinePayment);
    if (qrBase64) {
      try {
        const qrImageId = workbook.addImage({
          base64: qrBase64,
          extension: 'png',
        });
        worksheet.addImage(qrImageId, {
          tl: { col: 8.0, row: 0.35 },
          ext: { width: 90, height: 90 }
        });
      } catch (e) {
        console.error("Error adding QR image to sheet:", e);
      }
    }

    addEmptyRow();

    // --- Meta Info ---
    const rowMeta1 = worksheet.addRow([`Họ tên: ${invoiceInfo.fullName} (${invoiceInfo.studentCode || ''})`, null, null, null, null, null, `Mã: ..................`]);
    worksheet.mergeCells(`A${rowMeta1.number}:F${rowMeta1.number}`);
    worksheet.mergeCells(`G${rowMeta1.number}:I${rowMeta1.number}`);
    rowMeta1.getCell(1).font = { name: 'Times New Roman', bold: true, size: 12 };
    rowMeta1.getCell(7).font = { name: 'Times New Roman', bold: true, size: 12 };

    const rowMeta2 = worksheet.addRow([`Lớp: ${invoiceInfo.className || ''}`, null, null, null, null, null, `Số: ..................`]);
    worksheet.mergeCells(`A${rowMeta2.number}:F${rowMeta2.number}`);
    worksheet.mergeCells(`G${rowMeta2.number}:I${rowMeta2.number}`);
    rowMeta2.getCell(1).font = { name: 'Times New Roman', bold: true, size: 12 };
    rowMeta2.getCell(7).font = { name: 'Times New Roman', bold: true, size: 12 };

    const rowMeta3 = worksheet.addRow([`SĐT: ${invoiceInfo.parentPhone || ''}`, null, null, null, null, null, `Quyển: ..................`]);
    worksheet.mergeCells(`A${rowMeta3.number}:F${rowMeta3.number}`);
    worksheet.mergeCells(`G${rowMeta3.number}:I${rowMeta3.number}`);
    rowMeta3.getCell(1).font = { name: 'Times New Roman', bold: true, size: 12 };
    rowMeta3.getCell(7).font = { name: 'Times New Roman', bold: true, size: 12 };

    addEmptyRow();

    // --- Table Headers ---
    const headerRow = worksheet.addRow(["STT", "Tên khoản thu", "ĐVT", "SL", "Đơn giá", "Thuế", "Giảm giá", "Thành tiền", "Ghi chú"]);
    headerRow.eachCell((cell) => {
      cell.font = { name: 'Times New Roman', bold: true, size: 12 };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' }, left: { style: 'thin' },
        bottom: { style: 'thin' }, right: { style: 'thin' }
      };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
    });

    // --- Table Data ---
    const details = invoiceInfo.invoiceDetails || [];
    details.forEach(item => {
      let stt = item._isChild ? '' : (item._displayIndex || '');
      let name = item._isChild ? `- ${item.revenueName}` : item.revenueName;
      let row = worksheet.addRow([
        stt,
        name,
        item.unit || '',
        item.quantity ?? '',
        formatCurrency(item.price),
        item._isChild ? '' : formatTax(item.taxPercentage),
        item.discountNumber ? formatCurrency(item.discountNumber) : '',
        formatCurrency(item.amount),
        item.description || ''
      ]);
      
      row.eachCell((cell, colNumber) => {
        cell.font = { 
          name: 'Times New Roman', 
          size: 12,
          italic: item._isChild ? true : false,
          bold: colNumber === 8 && !item._isChild ? true : false
        };
        cell.border = {
          top: { style: 'thin' }, left: { style: 'thin' },
          bottom: { style: 'thin' }, right: { style: 'thin' }
        };
        if ([1, 3, 4].includes(colNumber)) cell.alignment = { horizontal: 'center', vertical: 'middle' };
        if ([5, 6, 7, 8].includes(colNumber)) cell.alignment = { horizontal: 'right', vertical: 'middle' };
        if (colNumber === 9) cell.alignment = { horizontal: 'left', vertical: 'middle' };
      });
      if (item.quantity !== null && item.quantity !== undefined) {
        row.getCell(4).numFmt = '0.##';
      }
    });

    // --- Summary Table ---
    const addSummaryRow = (label, value, bold = false) => {
      const row = worksheet.addRow([null, null, null, null, label, null, null, null, value]);
      worksheet.mergeCells(`E${row.number}:H${row.number}`);
      row.getCell(5).font = { name: 'Times New Roman', bold, size: 12 };
      row.getCell(5).alignment = { horizontal: 'right', vertical: 'middle' };
      row.getCell(9).font = { name: 'Times New Roman', bold, size: 12, color: bold && label.includes('thiếu') ? { argb: 'FFDC2626' } : bold && label.includes('thừa') ? { argb: 'FF15803D' } : {} };
      row.getCell(9).alignment = { horizontal: 'right', vertical: 'middle' };
      
      // Border
      [5, 6, 7, 8, 9].forEach(col => {
        row.getCell(col).border = {
          top: { style: 'thin' }, left: { style: 'thin' },
          bottom: { style: 'thin' }, right: { style: 'thin' }
        };
      });
      return row;
    };

    const total = details.filter(item => !item._isChild).reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const afterMoney = Number(invoiceInfo.afterMoney || 0);

    addSummaryRow("Tổng cộng", formatCurrency(total), true);
    const beforeMoney = Number(invoiceInfo.beforeMoney || 0);
    if (beforeMoney >= 0) {
      addSummaryRow("Tiền thừa phiếu thu trước", formatCurrency(beforeMoney));
    } else {
      addSummaryRow("Tiền thiếu phiếu thu trước", `(${formatCurrency(Math.abs(beforeMoney))})`);
    }
    addSummaryRow("Số tiền đã trả", formatCurrency(invoiceInfo.collectedMoney || 0));
    const balanceLabel = afterMoney > 0 ? "Số tiền còn thừa" : afterMoney < 0 ? "Số tiền còn thiếu" : "Số tiền còn lại";
    addSummaryRow(balanceLabel, formatCurrency(Math.abs(afterMoney)), true);

    const wordsAmountRow = worksheet.addRow([null, null, null, null, `Bằng chữ: ${calcWords(afterMoney)}`]);
    worksheet.mergeCells(`E${wordsAmountRow.number}:I${wordsAmountRow.number}`);
    wordsAmountRow.getCell(5).font = { name: 'Times New Roman', italic: true, size: 12 };
    wordsAmountRow.getCell(5).alignment = { horizontal: 'right', vertical: 'middle' };
    [5, 6, 7, 8, 9].forEach(col => {
      wordsAmountRow.getCell(col).border = {
        top: { style: 'thin' }, left: { style: 'thin' },
        bottom: { style: 'thin' }, right: { style: 'thin' }
      };
      wordsAmountRow.getCell(col).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
    });

    addEmptyRow();

    // --- Footer ---
    const notesRow = worksheet.addRow(["Ghi chú: PHHS vui lòng thanh toán tại văn phòng trường hoặc qua tài khoản ngân hàng của nhà trường trước ngày mùng 10 hàng tháng! Trân trọng cảm ơn."]);
    notesRow.getCell(1).font = { name: 'Times New Roman', italic: true, size: 11 };
    worksheet.mergeCells(`A${notesRow.number}:I${notesRow.number}`);
    
    addEmptyRow();
    
    if (bankAccount || bankName) {
      const bankRow1 = worksheet.addRow([`STK: ${bankAccount || '......................'}`, null, null, null, null, null, null, `Ngân hàng: ${bankName || '......................'}`]);
      worksheet.mergeCells(`A${bankRow1.number}:F${bankRow1.number}`);
      worksheet.mergeCells(`H${bankRow1.number}:I${bankRow1.number}`);
      bankRow1.getCell(1).font = { name: 'Times New Roman', bold: true, size: 11 };
      bankRow1.getCell(8).font = { name: 'Times New Roman', bold: true, size: 11 };

      const bankRow2 = worksheet.addRow([`Chủ TK: ${bankOwner || '......................'}`]);
      worksheet.mergeCells(`A${bankRow2.number}:I${bankRow2.number}`);
      bankRow2.getCell(1).font = { name: 'Times New Roman', bold: true, size: 11 };
    }
    
    addEmptyRow();

    let dateText = `Ngày ${moment().format('DD')} tháng ${moment().format('MM')} năm ${moment().format('YYYY')}`;
    if (showTime) {
      dateText = `${moment().format('HH:mm')}, Ngày ${moment().format('DD')} tháng ${moment().format('MM')} năm ${moment().format('YYYY')}`;
    }
    const signTimeRow = worksheet.addRow([null, null, null, null, null, null, null, dateText]);
    worksheet.mergeCells(`G${signTimeRow.number}:I${signTimeRow.number}`);
    signTimeRow.getCell(8).font = { name: 'Times New Roman', italic: true, size: 11 };
    signTimeRow.getCell(8).alignment = { horizontal: 'center' };

    const signTitleRow = worksheet.addRow([null, "Người nộp tiền", null, null, null, null, null, "Người lập phiếu"]);
    worksheet.mergeCells(`B${signTitleRow.number}:E${signTitleRow.number}`);
    worksheet.mergeCells(`G${signTitleRow.number}:I${signTitleRow.number}`);
    signTitleRow.getCell(2).font = { name: 'Times New Roman', bold: true, size: 12 };
    signTitleRow.getCell(2).alignment = { horizontal: 'center' };
    signTitleRow.getCell(8).font = { name: 'Times New Roman', bold: true, size: 12 };
    signTitleRow.getCell(8).alignment = { horizontal: 'center' };

    const signSubRow = worksheet.addRow([null, "(Ký, họ tên)", null, null, null, null, null, "(Ký, họ tên)"]);
    worksheet.mergeCells(`B${signSubRow.number}:E${signSubRow.number}`);
    worksheet.mergeCells(`G${signSubRow.number}:I${signSubRow.number}`);
    signSubRow.getCell(2).font = { name: 'Times New Roman', italic: true, size: 11 };
    signSubRow.getCell(2).alignment = { horizontal: 'center' };
    signSubRow.getCell(8).font = { name: 'Times New Roman', italic: true, size: 11 };
    signSubRow.getCell(8).alignment = { horizontal: 'center' };

    // Padding cho chữ ký
    addEmptyRow();
    addEmptyRow();
    addEmptyRow();
    addEmptyRow();

    const signNameRow = worksheet.addRow([null, null, null, null, null, null, null, invoiceInfo.createdBy || ""]);
    worksheet.mergeCells(`G${signNameRow.number}:I${signNameRow.number}`);
    signNameRow.getCell(8).font = { name: 'Times New Roman', bold: true, size: 12 };
    signNameRow.getCell(8).alignment = { horizontal: 'center' };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const timeStamp = moment().format('YYYYMMDD_HHmmss');
  saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Phieu_Thu_Hoc_Phi_${timeStamp}.xlsx`);
}
